import dayjs from "dayjs";
import { getDoctorById } from "../../services/doctorService";
import { getPrescriptionsByPatientId } from "../../services/prescriptionService";
import {
  filterSubCategoryResultsForTest,
  hasSubCategories,
  parseSubCategoryResults,
  resolveDisplaySubCategories,
  resolveLabTestReferenceMeta,
} from "../PathManagePatient/labTestWorkflowUtils";

const EMPTY_LAB_DISPLAY_VALUES = new Set(["—", "-", "n/a", "na"]);

/** Blank cell when value is missing or a placeholder dash. */
export const formatLabReportCellValue = (value) => {
  if (value == null) return "";
  const text = String(value).trim();
  if (!text) return "";
  if (EMPTY_LAB_DISPLAY_VALUES.has(text.toLowerCase())) return "";
  return text;
};

/** Ref. Value column: show unit in parentheses only when both exist. */
export const formatLabReportReferenceDisplay = (referenceValue, unit) => {
  const ref = formatLabReportCellValue(referenceValue);
  const unitText = formatLabReportCellValue(unit);
  if (!ref && !unitText) return "";
  if (ref && unitText) return `${ref} (${unitText})`;
  return ref || unitText;
};

const formatDoctorName = (doctor) => {
  if (!doctor) return "";
  if (doctor.name && String(doctor.name).trim()) return String(doctor.name).trim();
  const fullName = `${doctor.firstName || ""} ${doctor.lastName || ""}`.trim();
  if (!fullName) return "";
  return fullName.toLowerCase().startsWith("dr") ? fullName : `Dr. ${fullName}`;
};

const isUsableDoctorName = (name) => {
  if (name == null) return false;
  const text = String(name).trim();
  if (!text) return false;
  const lower = text.toLowerCase();
  return lower !== "n/a" && lower !== "null" && lower !== "—" && lower !== "-";
};

export const resolveReferringDoctorName = (report, fallbackDoctorName = "") => {
  const candidates = [
    report?.doctorName,
    report?.resolvedDoctorName,
    report?.assignedByName,
    fallbackDoctorName,
  ];
  const resolved = candidates.find((name) => isUsableDoctorName(name));
  return resolved ? String(resolved).trim() : "";
};

export const enrichReportsWithDoctorNames = async (reports, patientId, appointment) => {
  if (!Array.isArray(reports) || reports.length === 0) return reports;

  const doctorCache = new Map();
  const uniqueDoctorIds = [
    ...new Set(
      reports
        .flatMap((r) => [r.doctorId, r.assignedBy])
        .filter((id) => id != null && id !== "")
        .map(Number)
        .filter((id) => !Number.isNaN(id))
    ),
  ];

  await Promise.all(
    uniqueDoctorIds.map(async (doctorId) => {
      try {
        const doctor = await getDoctorById(doctorId);
        const name = formatDoctorName(doctor);
        if (name) doctorCache.set(doctorId, name);
      } catch (_) {
        // ignore lookup failures
      }
    })
  );

  let prescriptionDoctorFallback = "";
  let prescriptionDoctorIdFallback = null;
  const prescriptionDoctorByTestId = new Map();
  const prescriptionDoctorByTestName = new Map();
  const normalizeName = (value) => (value || "").toString().trim().toLowerCase();

  if (patientId) {
    try {
      const prescriptions = await getPrescriptionsByPatientId(patientId);
      if (Array.isArray(prescriptions) && prescriptions.length > 0) {
        const sorted = [...prescriptions].sort((a, b) => {
          const aDate = new Date(a.createdAt || a.prescriptionDate || 0).getTime();
          const bDate = new Date(b.createdAt || b.prescriptionDate || 0).getTime();
          return bDate - aDate;
        });

        const withDoctor = sorted.find((row) => isUsableDoctorName(row?.doctorName));
        prescriptionDoctorFallback = withDoctor?.doctorName || "";
        prescriptionDoctorIdFallback = withDoctor?.doctorId || null;

        if (!prescriptionDoctorFallback && prescriptionDoctorIdFallback) {
          try {
            const doctor = await getDoctorById(prescriptionDoctorIdFallback);
            prescriptionDoctorFallback = formatDoctorName(doctor);
          } catch (_) {
            // ignore
          }
        }

        sorted.forEach((prescription) => {
          const doctorName = isUsableDoctorName(prescription?.doctorName)
            ? prescription.doctorName
            : "";
          if (!doctorName) return;

          (prescription.patientLabTests || []).forEach((labTest) => {
            const testId = labTest.testId || labTest.id || labTest.labTestId;
            const testName = normalizeName(
              labTest.testName || labTest.test_name || labTest.name || labTest.labTestName
            );
            if (testId != null && testId !== "") {
              prescriptionDoctorByTestId.set(Number(testId), doctorName);
            }
            if (testName && !prescriptionDoctorByTestName.has(testName)) {
              prescriptionDoctorByTestName.set(testName, doctorName);
            }
          });
        });
      }
    } catch (_) {
      // ignore prescription lookup failures
    }
  }

  // Org completed/billed rows sometimes carry doctorName — use as same-patient fallback.
  const orgDoctorFallback = reports.find((r) => isUsableDoctorName(r?.doctorName))?.doctorName || "";

  return reports.map((report) => {
    const reportTestName = normalizeName(
      report.testName || report.test_name || report.name || report.labTestName
    );
    const doctorId = report.doctorId || report.assignedBy || null;
    const resolvedDoctorName =
      (isUsableDoctorName(report.doctorName) ? report.doctorName : "") ||
      (doctorId ? doctorCache.get(Number(doctorId)) : "") ||
      (isUsableDoctorName(report.assignedByName) ? report.assignedByName : "") ||
      (report.testId ? prescriptionDoctorByTestId.get(Number(report.testId)) : "") ||
      (reportTestName ? prescriptionDoctorByTestName.get(reportTestName) : "") ||
      prescriptionDoctorFallback ||
      orgDoctorFallback ||
      (isUsableDoctorName(appointment?.doctorName) ? appointment.doctorName : "") ||
      "";

    return {
      ...report,
      resolvedDoctorName: String(resolvedDoctorName || "").trim(),
      doctorName: String(resolvedDoctorName || report.doctorName || "").trim(),
    };
  });
};

export const formatReportDateOnly = (reportDate) => {
  if (!reportDate) return "—";
  const datePart = String(reportDate).split("T")[0];
  return dayjs(datePart).isValid() ? dayjs(datePart).format("DD MMM YYYY") : "—";
};

export const parseLabReportResult = (result) => {
  const parsed = parseSubCategoryResults(result);
  if (parsed?.subCategoryResults && Object.keys(parsed.subCategoryResults).length > 0) {
    return {
      subCategoryResults: parsed.subCategoryResults,
      remarks: parsed.remarks || "",
    };
  }

  if (!result) {
    return { simpleResult: "", remarks: "" };
  }

  if (typeof result === "string") {
    try {
      const obj = JSON.parse(result);
      if (obj?.result != null && String(obj.result).trim() !== "") {
        return {
          simpleResult: String(obj.result),
          remarks: obj.remarks || obj.r || "",
        };
      }
    } catch (_) {
      return { simpleResult: result, remarks: "" };
    }
  }

  return { simpleResult: String(result), remarks: "" };
};

const buildRowsFromResultMap = (resultMap, templateSubs = []) => {
  const rows = [];

  if (templateSubs.length > 0) {
    // Structured tests — show template fields (predefined or org/custom catalog).
    templateSubs.forEach((sub) => {
      rows.push({
        name: sub.name,
        result: formatLabReportCellValue(resultMap[sub.name]),
        referenceValue: formatLabReportCellValue(sub.referenceValue),
        unit: formatLabReportCellValue(sub.unit),
      });
    });
    return rows;
  }

  Object.entries(resultMap || {}).forEach(([key, value]) => {
    rows.push({
      name: key,
      result: formatLabReportCellValue(value),
      referenceValue: "",
      unit: "",
    });
  });

  return rows;
};

export const getResultDisplayRows = (testName, result, options = {}) => {
  const parsed = parseLabReportResult(result);
  const catalogSubs = Array.isArray(options.subCategories) ? options.subCategories : [];
  // Prefer org/custom catalog subCategories (with reference values); else hardcoded predefined.
  const displaySubs = resolveDisplaySubCategories(
    testName,
    catalogSubs.length > 0 ? { subCategories: catalogSubs } : null
  );

  if (parsed.subCategoryResults) {
    let resultMap = parsed.subCategoryResults;
    if (displaySubs.length > 0) {
      const allowed = new Set(displaySubs.map((sub) => sub.name));
      resultMap = Object.fromEntries(
        Object.entries(parsed.subCategoryResults).filter(([key]) => allowed.has(key))
      );
    } else if (hasSubCategories(testName)) {
      resultMap = filterSubCategoryResultsForTest(testName, parsed.subCategoryResults);
    }
    const rows = buildRowsFromResultMap(resultMap, displaySubs);

    return {
      type: "table",
      rows,
      remarks: parsed.remarks || "",
    };
  }

  // Standalone / custom single-parameter tests (HDL, TSH, Blood Group, …)
  const refMeta = resolveLabTestReferenceMeta({
    testName,
    report: {
      referenceValue: options.referenceValue,
      normalRange: options.normalRange,
      unit: options.unit,
    },
    subCategories: catalogSubs,
  });

  return {
    type: "simple",
    simpleResult: formatLabReportCellValue(parsed.simpleResult),
    remarks: parsed.remarks || "",
    rows: [],
    normalRange: formatLabReportCellValue(refMeta.referenceValue),
    unit: formatLabReportCellValue(refMeta.unit),
    referenceDisplay: formatLabReportReferenceDisplay(refMeta.referenceValue, refMeta.unit),
  };
};

export const buildPathologyLabReportHtml = ({
  report,
  patient,
  organizationName,
  hospitalAddress,
  hospitalPhone,
  referringDoctorName,
}) => {
  const display = getResultDisplayRows(report.testName, report.result, {
    subCategories: report.subCategories,
    normalRange: report.normalRange,
    referenceValue: report.referenceValue,
    unit: report.unit,
  });
  const reportDate = formatReportDateOnly(report.reportDate);

  const patientId = patient?.id || report.patientId || "—";
  const patientName = patient?.name || report.patientName || "—";
  const patientMobile = patient?.mobile || "—";
  const patientAge =
    patient?.dob && dayjs(patient.dob).isValid()
      ? `${Math.max(0, dayjs().diff(dayjs(patient.dob), "year"))} Yrs.`
      : "—";
  const patientGender = patient?.gender || "—";
  const doctorName = resolveReferringDoctorName(report, referringDoctorName);
  const reportMeta = `Report ID: ${report.reportId || "—"} | Report Date: ${reportDate} | Status: ${report.resultStatus || "—"}`;

  const resultTableRows =
    display.type === "table"
      ? display.rows
          .map(
            (row) => `
          <tr>
            <td>${row.name}</td>
            <td>${row.result}</td>
            <td>${formatLabReportReferenceDisplay(row.referenceValue, row.unit)}</td>
          </tr>`
          )
          .join("")
      : `<tr>
          <td>${report.testName}</td>
          <td>${display.simpleResult}</td>
          <td>${
            display.referenceDisplay ||
            formatLabReportReferenceDisplay(
              display.normalRange || report.normalRange || report.referenceValue,
              display.unit || report.unit
            )
          }</td>
        </tr>`;

  const remarksSection = display.remarks
    ? `<div class="remarks"><strong>Remarks:</strong> ${display.remarks}</div>`
    : "";

  const title = `Pathology Report - ${report.testName}`;
  const documentHtml = `
    <html>
      <head>
        <title>${title}</title>
        <style>
          * { box-sizing: border-box; }
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            color: #0b4f8a;
          }
          .report {
            max-width: 820px;
            margin: 0 auto;
            border: 2px solid #0b4f8a;
            padding: 18px 20px 24px;
          }
          .header {
            position: relative;
            text-align: center;
            margin-bottom: 14px;
          }
          .header h1 {
            margin: 0;
            font-family: "Times New Roman", Georgia, serif;
            font-size: 28px;
            font-weight: 700;
          }
          .header .address, .header .phone {
            margin: 6px 0 0;
            font-size: 13px;
          }
          .header .phone {
            position: absolute;
            top: 4px;
            right: 0;
            font-weight: 600;
          }
          .report-title {
            text-align: center;
            font-size: 15px;
            font-weight: 700;
            text-decoration: underline;
            margin: 12px 0 18px;
          }
          .patient-row, .patient-details {
            display: flex;
            justify-content: space-between;
            gap: 12px;
            font-size: 13px;
            margin-bottom: 10px;
            flex-wrap: wrap;
          }
          .patient-details {
            display: grid;
            grid-template-columns: 2fr 1.2fr 0.8fr 0.8fr;
            gap: 12px;
            margin-bottom: 16px;
          }
          .field-label { font-weight: 600; }
          .field-value {
            display: inline-block;
            min-width: 80px;
            border-bottom: 1px dotted #0b4f8a;
            padding: 0 4px 2px;
          }
          .test-name-row {
            display: flex;
            justify-content: space-between;
            align-items: baseline;
            gap: 12px;
            margin: 12px 0 8px;
            flex-wrap: wrap;
          }
          .test-name {
            font-size: 14px;
            font-weight: 700;
            text-transform: uppercase;
          }
          .test-meta {
            font-size: 11px;
            font-weight: 600;
            color: #2f5f8f;
            text-align: right;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            border: 1px solid #0b4f8a;
            padding: 8px 10px;
            text-align: left;
            font-size: 13px;
          }
          th {
            background: #eef5fb;
            font-weight: 700;
          }
          .remarks {
            margin-top: 14px;
            padding: 10px 12px;
            border: 1px solid #0b4f8a;
            font-size: 13px;
            background: #f8fbff;
          }
          .disclaimer {
            margin-top: 36px;
            font-size: 12px;
            color: #0b4f8a;
            line-height: 1.5;
            text-align: center;
          }
          .disclaimer p {
            margin: 4px 0;
          }
          .footer {
            margin-top: 24px;
            text-align: center;
            font-size: 12px;
            color: #4b6f8f;
            border-top: 1px dashed #7aa6cc;
            padding-top: 12px;
          }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <div class="report">
          <div class="header">
            ${hospitalPhone ? `<div class="phone">Phone : ${hospitalPhone}</div>` : ""}
            <h1>${(organizationName || "Hospital").toUpperCase()}</h1>
            ${hospitalAddress ? `<p class="address">${hospitalAddress}</p>` : ""}
          </div>

          <div class="report-title">PATHOLOGY REPORT</div>

          <div class="patient-row">
            <div>
              <span class="field-label">Patient ID :</span>
              <span class="field-value">${patientId}</span>
            </div>
            <div>
              <span class="field-label">Ref. by Medical Officer :</span>
              <span class="field-value">${doctorName}</span>
            </div>
          </div>

          <div class="patient-details">
            <div>
              <span class="field-label">Name of Patient</span>
              <span class="field-value">${patientName}</span>
            </div>
            <div>
              <span class="field-label">Mob. No.</span>
              <span class="field-value">${patientMobile}</span>
            </div>
            <div>
              <span class="field-label">Age</span>
              <span class="field-value">${patientAge}</span>
            </div>
            <div>
              <span class="field-label">Sex</span>
              <span class="field-value">${patientGender}</span>
            </div>
          </div>

          <div class="test-name-row">
            <div class="test-name">${report.testName}</div>
            <div class="test-meta">${reportMeta}</div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Investigation</th>
                <th>Result</th>
                <th>Ref. Value</th>
              </tr>
            </thead>
            <tbody>
              ${resultTableRows}
            </tbody>
          </table>

          ${remarksSection}

          <div class="disclaimer">
            <p>Report is computerized, no signature required.</p>
            <p>This report is not for medicolegal use.</p>
          </div>

          <div class="footer">
            <p>Generated on ${dayjs().format("DD MMM YYYY HH:mm")}</p>
          </div>
        </div>
      </body>
    </html>
  `;

  return { title, documentHtml };
};

export const printPathologyLabReport = (options) => {
  const { title, documentHtml } = buildPathologyLabReportHtml(options);
  const printWindow = window.open("", "_blank");
  if (!printWindow) return;
  printWindow.document.write(documentHtml);
  printWindow.document.close();
  printWindow.print();
  return title;
};
